import { useAccountState } from "../../redux/reducers/accountReducer";
import { useApi } from "../api/useApi";
import { Note, UserAccount } from "../types";
import { useCaseNoteSignalR } from "../../signalR/useCaseNoteSignalR";
import { useCaseNoteState } from "../../redux/reducers/caseNoteReducer";
import { useCaseNoteActions } from "../../redux/actions/caseNoteActions";
import { useToasts } from "../../components/toasts/useToasts";

export const CaseNoteApiRoutes = {
    getCaseNotes: (accountId: string, caseId: string) => `account/${accountId}/case/${caseId}`,
    getCaseNotesForTask: (accountId: string, caseId: string, taskId: string) => `account/${accountId}/case/${caseId}/task/${taskId}`,
    getCaseNotesForDocument: (accountId: string, caseId: string, docId: string) => `account/${accountId}/case/${caseId}/document/${docId}`,
    getCaseNotesForEvent: (accountId: string, caseId: string, eventId: string) => `account/${accountId}/case/${caseId}/event/${eventId}`,
    upsertCaseNote: (accountId: string, caseId: string) => `account/${accountId}/case/${caseId}/note`,
    upsertCaseNotes: (accountId: string, caseId: string) => `account/${accountId}/case/${caseId}/notes`,
    removeCaseNote: (accountId: string, caseId: string, noteId: string) => `account/${accountId}/case/${caseId}/note/${noteId}`,
}

export type CaseNoteService = {
    notes: Note[];
    getCaseNotesForTaskAsync: (taskId: string) => Promise<Note[]>,
    getCaseNotesForDocumentAsync: (docId: string) => Promise<Note[]>,
    getCaseNotesForEventAsync: (eventId: string) => Promise<Note[]>,
    upsertCaseNoteAsync: (caseNote: Note) => Promise<void>,
    upsertCaseNotesAsync: (caseNotes: Note[]) => Promise<void>,
    deleteCaseNoteAsync: (noteId: string) => Promise<void>,
    loadNotesAsync: () => Promise<void>;
}
export type CaseNoteServiceOptions = {
    taskId?: string;
    docId?: string;
    eventId?: string;
}
/**
 * This service will load notes for a specific case and provides methods to interact with them
 * @param caseId The ID of the case for which to load notes
 * @returns
 */
export const useCaseNoteService = (caseId?: string): CaseNoteService => {

    //Hooks
    const { primaryAccount } = useAccountState();
    const { account } = primaryAccount as UserAccount ?? {};
    const caseNoteState = useCaseNoteState();
    const caseNoteActions = useCaseNoteActions();
    const toasts = useToasts();

    //Api
    const api = useApi<Note>('api/CaseNote');

    //Data Calls
    const loadNotesForCaseAsync = async () => {
        caseNoteActions.setCaseNotes([]);
        const notes = await api.getAsync(CaseNoteApiRoutes.getCaseNotes(account.accountId as string, caseId as string));
        caseNoteActions.setCaseNotes(notes);
    }
    const getCaseNotesForTaskAsync = async (taskId: string) => {
        const notes = await api.getAsync(CaseNoteApiRoutes.getCaseNotesForTask(account.accountId as string, caseId as string, taskId));
        return notes;
    }
    const getCaseNotesForDocumentAsync = async (docId: string) => {
        const notes = await api.getAsync(CaseNoteApiRoutes.getCaseNotesForDocument(account.accountId as string, caseId as string, docId));
        return notes;
    }
    const getCaseNotesForEventAsync = async (eventId: string) => {
        const notes = await api.getAsync(CaseNoteApiRoutes.getCaseNotesForEvent(account.accountId as string, caseId as string, eventId));
        return notes;
    }
    const upsertCaseNoteAsync = async (note: Note) => {
        caseNoteActions.addOrReplaceCaseNote(note);
        const updatedNote = await api.postAsync(note, CaseNoteApiRoutes.upsertCaseNote(account.accountId as string, caseId as string));
        caseNoteActions.addOrReplaceCaseNote(updatedNote);
    }
    interface CaseNoteUpsertResult {
            statusCode: number;
            message?: string;
            resource?: Note;
        }
    const upsertCaseNotesAsync = async (notes: Note[]) => {
        caseNoteActions.addOrReplaceCaseNotes(notes);
        const updatedNoteResults: CaseNoteUpsertResult[] = await api.postAsync(notes, CaseNoteApiRoutes.upsertCaseNotes(account.accountId as string, caseId as string));
        caseNoteActions.addOrReplaceCaseNotes(
            updatedNoteResults.map((r) => r.resource as Note).filter((t) => t !== undefined),
        );
        const createdNotes = updatedNoteResults.filter((r) => r.statusCode == 201);
        const createdText = createdNotes.length > 0 ? `${createdNotes.length} note(s) added` : '';
        toasts.notifySuccess(
            'Notes/Comments added',
            `${createdText}`,
        );
    }
    const deleteCaseNoteAsync = async (noteId: string) => {
        caseNoteActions.removeCaseNote(noteId);
        await api.deleteAsync([noteId], CaseNoteApiRoutes.removeCaseNote(account.accountId as string, caseId as string, noteId));
    }    

    //Logic
    const sortNotes = (a: Note, b: Note) => {
        const aDate = !!a.lifecycle?.createdOn ? new Date(a.lifecycle.createdOn).getTime() : 0;
        const bDate = !!b.lifecycle?.createdOn ? new Date(b.lifecycle.createdOn).getTime() : 0;
        return bDate - aDate
    }
       
    return {
        notes: [...caseNoteState.notes || []].sort(sortNotes),
        loadNotesAsync: loadNotesForCaseAsync,
        getCaseNotesForTaskAsync,
        getCaseNotesForDocumentAsync,
        getCaseNotesForEventAsync,
        upsertCaseNoteAsync,
        upsertCaseNotesAsync,
        deleteCaseNoteAsync,
    }
}
